import { hasVisibleDirtyChanges } from "@frui.ts/dirtycheck";
import format from "date-fns/format";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import { startOfCzechDay } from "./dateUtils";
import omitValidationAndDirtyCheck from "./omitValidationAndDirtyCheck";
import snakeCaseKeys from "./snakeCaseKeys";
import { formatPrefixNumber } from "@edaz/utils/bankAccount";

export const FILTER_DATE_FORMAT = "yyyy-MM-dd";

export function omitFulltext(entity: Record<string, any>) {
  return omit(entity, ["fullTextSearchEnabled"]);
}

export function isFilterEmpty(filter: Record<string, any> | any, omitFullTextEnabled = true) {
  let omittedFilter = omitValidationAndDirtyCheck(filter);
  if (omitFullTextEnabled) {
    omittedFilter = omitFulltext(omittedFilter);
  }
  return Object.values(omittedFilter).every(item => item === undefined);
}

export function isFilterDirty(filter: Record<string, any> | any) {
  return hasVisibleDirtyChanges(filter);
}

export function isNumberValue(value: string) {
  return /^-{0,1}[0-9]+(\.[0-9]+)*$/.test(value);
}

export function isUndefinedValue(value: string) {
  return /^undefined$/.test(value);
}

export function isDateValue(value: string) {
  const mathFormat = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value);

  if (mathFormat) {
    const date = parseISO(value);
    return date.toString() !== "Invalid Date";
  }

  return false;
}

export function isNestedObjectValue(value: string) {
  return value.includes("&");
}

export function excludeEmptyValues(filters: Record<string, any>) {
  return mapValues(filters, value => (value === "" ? undefined : value));
}

export function formatDateValues(filters: Record<string, any>) {
  return mapValues(filters, value => {
    if (value instanceof Object) {
      value = formatDateObjectValues(value);
    }
    if (value instanceof Date) {
      value = format(value, FILTER_DATE_FORMAT);
    }
    return value;
  });
}

// tslint:disable-next-line:bool-param-default
export function serializeAccountNumber(account?: string, fullTextSearchEnabled?: boolean) {
  if (account?.includes("-")) {
    const [prefix, accountNumber] = account.split("-");
    const formattedPrefix = formatPrefixNumber(prefix);

    if (formattedPrefix) {
      return `${formattedPrefix}${fullTextSearchEnabled ? accountNumber : accountNumber.padStart(10, "0")}`;
    } else {
      return `${accountNumber.replace(/^0+/, "")}`;
    }
  } else if (account) {
    return `${account.replace(/^0+/, "")}`;
  }

  return undefined;
}

export function formatAccountValues(filters: Record<string, any>) {
  const fullTextSearchEnabled = filters?.fullTextSearchEnabled || false;
  if (filters.hasOwnProperty("accountNumber")) {
    filters.accountNumber = serializeAccountNumber(filters.accountNumber, fullTextSearchEnabled);
  }

  if (filters.hasOwnProperty("account_number")) {
    filters.account_number = serializeAccountNumber(filters.account_number, fullTextSearchEnabled);
  }

  if (filters.hasOwnProperty("bankAccountNumber")) {
    filters.bankAccountNumber = serializeAccountNumber(filters.bankAccountNumber, fullTextSearchEnabled);
  }

  if (filters.hasOwnProperty("recipientAccountNumber")) {
    filters.recipientAccountNumber = serializeAccountNumber(filters.recipientAccountNumber, fullTextSearchEnabled);
  }

  if (filters.hasOwnProperty("senderAccountNumber")) {
    filters.senderAccountNumber = serializeAccountNumber(filters.senderAccountNumber, fullTextSearchEnabled);
  }

  if (filters.hasOwnProperty("bankAccount") && filters?.bankAccount) {
    filters.bankAccount = formatAccountValues(filters.bankAccount);
  }

  return filters;
}

export function formatIntervalObjectValues(filters: Record<string, any>) {
  return mapValues(filters, value => {
    if (value instanceof Object && !(value instanceof Date)) {
      return new URLSearchParams(formatDateValues(value)).toString();
    } else {
      return value;
    }
  });
}

export function formatDateObjectValues(object: any, formatString: string = FILTER_DATE_FORMAT) {
  Object.keys(object).forEach(key => {
    if (object[key] instanceof Date) {
      object[key] = format(object[key], formatString);
    }
  });
  return object;
}

export function parseNumberValues(filters: Record<string, any>) {
  return mapValues(filters, value => (isNumberValue(value) ? parseFloat(value) : value));
}

export function parseDateValue(value: string) {
  return isDateValue(value) ? startOfCzechDay(parse(value, FILTER_DATE_FORMAT, new Date())) : undefined;
}

export function parseDateValues(filters: Record<string, any>) {
  return mapValues(filters, value => (isDateValue(value) ? parseDateValue(value) : value));
}

export function parseUndefinedValues(filters: Record<string, any>) {
  return mapValues(filters, value => (isUndefinedValue(value) ? undefined : value));
}

export function parseIntervalObjectValue(value: string) {
  const urlParams = new URLSearchParams(value);
  const withParseValues = parseUndefinedValues(parseDateValues(parseNumberValues(Object.fromEntries(urlParams))));

  return mapValues(withParseValues, value =>
    value === undefined || value instanceof Date || typeof value === "number" ? value : undefined
  );
}

export function parseBankAccountObjectValue(value: string) {
  const urlParams = new URLSearchParams(value);
  return parseUndefinedValues(Object.fromEntries(urlParams));
}

export function serializeFilters(filters?: Record<string, any>, snakeCaseParams = true) {
  let filterParams = filters ?? {};
  if (snakeCaseParams) {
    filterParams = snakeCaseKeys(filterParams);
  }
  return formatAccountValues(formatDateValues(excludeEmptyValues(filterParams)));
}

export function trimObjectStrings(obj?: any) {
  for (const key in obj) {
    if (typeof obj[key] === "string") {
      obj[key] = obj[key].trim();
    }
  }
  return obj;
}
